// Zaman dönüştürme ve hesaplama yardımcı fonksiyonları

/**
 * UTC tarihini parse eder
 * @param utcDateString UTC formatında tarih string'i (örn: "2025-08-26T20:49:17.4973485")
 * @returns Date objesi
 */
export const convertUTCToLocal = (utcDateString: string): Date => {
  // API'den gelen tarihler her zaman timezone bilgisi olmadan gelir
  // UTC olarak işaretle ve parse et
  return new Date(utcDateString + 'Z');
};

/**
 * İki tarih arasındaki farkı hesaplar
 * @param targetDate Hedef tarih (başlama veya bitiş tarihi)
 * @param startDate Başlangıç tarihi (opsiyonel, varsayılan: şu an)
 * @returns Kalan süre objesi
 * 
 * Kullanım:
 * - Status 1 (Yaklaşan): calculateTimeRemaining(startDate) - müzayede ne zaman başlayacak
 * - Status 2 (Devam Eden): calculateTimeRemaining(endDate) - müzayede ne zaman bitecek
 */
export const calculateTimeRemaining = (targetDate: string, startDate?: string): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
} => {
  // UTC tarihlerini parse et
  const target = convertUTCToLocal(targetDate);
  const now = startDate ? convertUTCToLocal(startDate) : new Date();
  
  // UTC olarak karşılaştır
  const diffInMs = target.getTime() - now.getTime();
  
  if (diffInMs <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true
    };
  }
  
  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);
  
  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false
  };
};

/**
 * Auction tarihlerini debug etmek için yardımcı fonksiyon
 * @param auction Auction objesi
 * @returns Debug bilgileri
 */
export const debugAuctionDates = (auction: { id: string; status: number; startDate: string; endDate: string }) => {
  const now = new Date();
  const startDate = convertUTCToLocal(auction.startDate);
  const endDate = convertUTCToLocal(auction.endDate);
  
  // Log removed
  // Log removed
  // Log removed
  // Log removed
  
  if (auction.status === 1) {
    const timeToStart = calculateTimeRemaining(auction.startDate);
    // Log removed
  } else if (auction.status === 2) {
    const timeToEnd = calculateTimeRemaining(auction.endDate);
    // Log removed
  }
};

/**
 * UTC string'ini parse eder
 * @param dateString Tarih string'i (örn: "2025-08-26T20:49:17.4973485")
 * @returns Date objesi
 */
export const parseUTCDate = (dateString: string): Date => {
  // API'den gelen tarihler her zaman timezone bilgisi olmadan gelir
  // UTC olarak işaretle ve parse et
  return new Date(dateString + 'Z');
};

/**
 * Timezone debug fonksiyonu
 * @param utcDateString UTC formatında tarih string'i
 */
export const debugTimezone = (utcDateString: string) => {
  // Production ortamında log gösterme
  if (process.env.NODE_ENV === 'production') return;
  
  const localDate = convertUTCToLocal(utcDateString);
  
  // Log removed
  // Log removed
  // Log removed
  // Log removed
  // Log removed
  // Log removed
  // Log removed
};

/**
 * Tarihi formatlar
 * @param dateString Tarih string'i
 * @param locale Locale (varsayılan: tr-TR)
 * @returns Formatlanmış tarih string'i
 */
export const formatDate = (dateString: string, locale: string = 'tr-TR'): string => {
  const date = convertUTCToLocal(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Tarihi ve saati formatlar
 * @param dateString Tarih string'i
 * @param locale Locale (varsayılan: tr-TR)
 * @returns Formatlanmış tarih ve saat string'i
 */
export const formatDateTime = (dateString: string, locale: string = 'tr-TR'): string => {
  const date = convertUTCToLocal(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Tarihi kısa formatta formatlar
 * @param dateString Tarih string'i
 * @param locale Locale (varsayılan: tr-TR)
 * @returns Kısa formatlanmış tarih string'i
 */
export const formatShortDate = (dateString: string, locale: string = 'tr-TR'): string => {
  const date = convertUTCToLocal(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Saati formatlar
 * @param dateString Tarih string'i
 * @param locale Locale (varsayılan: tr-TR)
 * @returns Formatlanmış saat string'i
 */
export const formatTime = (dateString: string, locale: string = 'tr-TR'): string => {
  const date = convertUTCToLocal(dateString);
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  });
};
